import {
  getContractorLine,
  getContractorTrade,
  getLineItemName,
  getPartCostValue,
  type ScopeNavigatorContext,
} from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { formatUsd } from '@/Client/Presenters';
import {
  getEffectiveScanValueAsNumber,
  getLatestProgressStatus,
  getLineItemsInStandardSortOrder,
  getOptionsInStandardSortOrder,
  getTotalCostIfDefined,
  isLineItemLabor,
  isLineItemScoped,
  isOptionScoped,
  type ScanLineItemOptimized,
  type ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

import type { PhotoDetailModel } from '../PhotoDetail/photoDetailModels';
import type { TaskLineItemRow } from './models';

/**
 * Task fact rows for the photo detail overlay: status, labour basis, contractor, financials and
 * materials. The presenters shared with the ScopeNavigator sidebar live in
 * `@/Client/Lib/scopeNavigatorPartPresentersLib`.
 */

// ************************************************************************
// Work status (bespoke WorkStatus -> label/tone maps)
// ************************************************************************

type StatusTone = 'success' | 'info' | 'warning' | 'neutral';

const statusPresentation: Record<string, { label: string; tone: StatusTone }> = {
  completed: { label: 'Completed', tone: 'success' },
  inProgress: { label: 'In progress', tone: 'info' },
  needsRework: { label: 'Needs rework', tone: 'warning' },
  notStarted: { label: 'Not started', tone: 'neutral' },
};

export const getStatusPresentation = (task: ScanTaskOptimized | undefined): { label?: string; tone?: StatusTone } => {
  const status = getLatestProgressStatus(task);
  return status ? statusPresentation[status] ?? { label: status, tone: 'neutral' } : {};
};

// ************************************************************************
// Line items (per-lineItem breakdown from task.options[].lineItems[])
// ************************************************************************

/** Safe total cost for a line item (mirrors `getPartCostValue`'s reducedValues guard). */
const getLineItemCostValue = (lineItem: ScanLineItemOptimized): number | undefined => {
  if (lineItem.optimized && !lineItem.optimized.reducedValues) return undefined;
  return getTotalCostIfDefined(lineItem);
};

/** Material-only cost for a line item; undefined for labor-only (no material) line items. */
const getLineItemMaterialCostValue = (lineItem: ScanLineItemOptimized): number | undefined => lineItem.optimized?.reducedValues?.materialCost;

/**
 * What the labour total is calculated from — "14 LF × $344".
 *
 * The template is the source of truth here, not the mockup: a labour line item carries its own
 * quantity, its unit type and its unit price, and that pair is the nearest real equivalent of the
 * mockup's task-level `qty × rate`. A task with more than one labour line item has no single
 * basis to show, so it gets none rather than an invented one.
 */
export const getLaborBasisLabel = (task: ScanTaskOptimized | undefined): string | undefined => {
  if (!task) return undefined;
  const laborRows = getTaskLineItemRows(task).filter(row => row.isLabor);
  if (laborRows.length !== 1) return undefined;
  const [row] = laborRows;
  return row?.unitPriceLabel ? `${row.quantityLabel} × ${row.unitPriceLabel}` : undefined;
};

export const getTaskLineItemRows = (task: ScanTaskOptimized): readonly TaskLineItemRow[] => {
  if (!task.template) return [];
  const options = getOptionsInStandardSortOrder(task.template, task.options, isOptionScoped);
  return options.flatMap(option =>
    getLineItemsInStandardSortOrder(option.template, option.lineItems ?? [], isLineItemScoped)
      .map((lineItem): TaskLineItemRow => {
        const quantity = getEffectiveScanValueAsNumber(lineItem.values?.quantity) ?? 1;
        const unitType = lineItem.template?.values?.quantity?.type;
        const unitPrice = getEffectiveScanValueAsNumber(lineItem.values?.price);
        const totalCost = getLineItemCostValue(lineItem);
        const materialCost = getLineItemMaterialCostValue(lineItem);
        return {
          id: lineItem.id,
          optionId: option.id,
          name: getLineItemName(lineItem),
          productTitle: lineItem.skuDetails?.title,
          productSku: lineItem.skuDetails?.skus?.[0],
          productBrand: lineItem.skuDetails?.brand,
          quantity,
          quantityLabel: `${quantity} ${unitType || 'each'}`,
          unitPrice,
          unitPriceLabel: unitPrice != null ? formatUsd(unitPrice) : undefined,
          isLabor: isLineItemLabor(lineItem),
          materialCost,
          materialCostLabel: materialCost != null ? formatUsd(materialCost) : undefined,
          totalCost,
          totalCostLabel: totalCost != null ? formatUsd(totalCost) : undefined,
        };
      })
  );
};

// ************************************************************************
// Photo detail rows
// ************************************************************************

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);

const getMoneyLabel = (value: number | undefined): string => value != null ? formatCurrency(value) : '—';

export const getContractorRows = (task: ScanTaskOptimized | undefined, context?: ScopeNavigatorContext): PhotoDetailModel['contractor'] => {
  if (!task) return [];
  const laborCost = task.optimized?.reducedValues?.laborCost;
  return [
    { label: 'Name', value: getContractorLine(task, context) ?? '—' },
    { label: 'Trade', value: getContractorTrade(task, context) ?? '—' },
    { label: 'Labor rate', value: '—' },
    { label: 'Labor hrs', value: '—' },
    { label: 'Labor cost', value: getMoneyLabel(laborCost), tone: laborCost ? 'success' : 'neutral' },
  ];
};

export const getFinancialRows = (task: ScanTaskOptimized | undefined): PhotoDetailModel['financials'] => {
  if (!task) return [];
  const labor = task.optimized?.reducedValues?.laborCost;
  const materials = task.optimized?.reducedValues?.materialCost;
  return [
    { label: 'Labor', value: getMoneyLabel(labor) },
    { label: 'Materials', value: getMoneyLabel(materials) },
    { label: 'Task total', value: getMoneyLabel(getPartCostValue(task)), tone: 'success' },
  ];
};

export const getMaterialRows = (task: ScanTaskOptimized | undefined): PhotoDetailModel['materials'] => {
  if (!task) return [];
  // Per-line-item breakdown (task.options[].lineItems[]) restricted to
  // material-bearing rows: labor-only line items are excluded and the value
  // reflects material cost only (not material + labor) so this section is not
  // inflated by labor. Task-level labor stays in the FINANCIALS section.
  return getTaskLineItemRows(task)
    .filter(row => row.materialCost != null)
    .map(row => ({
      id: row.id,
      name: row.productTitle ?? row.name,
      vendorLabel: row.productBrand,
      quantityLabel: row.quantityLabel,
      valueLabel: row.materialCostLabel ?? getMoneyLabel(undefined),
      statusLabel: 'Listed',
    }));
};
